import React, { useMemo } from 'react';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { Modifier as ModifierData, ProductInterface } from '../../services/exports/Interfaces';
import { Radio } from '@mui/material';
import BackIcon from '../../assets/logo/BackIcon';
import Theme from '../Theme';
import NestedModifiers from './nested/NestedModifiers';
import { BasketLineItemModifier } from '../../store/types';
import ModifierToggleButton from './ModifierToggleButton';

interface Props {
  data: ModifierData | ProductInterface;
  quantity: number;
  isSelected: boolean;
  hasMultiply: boolean;
  isSingleSelect: boolean;
  toggle: (modifier: ModifierData | ProductInterface) => void;
  adjustQuantity: (step: number) => void;
  selectedModifiers: BasketLineItemModifier[];
  isIncreasable: boolean;
}

export default function Modifier(props: Props) {
  const {
    data,
    isSelected,
    isSingleSelect,
    toggle,
    quantity,
    adjustQuantity,
    selectedModifiers,
    isIncreasable,
    hasMultiply,
  } = props;
  const { formatCurrency } = useAppMethods();
  const canBeIncreased = (isIncreasable && !isSelected) || (isSelected && isIncreasable && hasMultiply);
  const hasEditButton = !isSelected && !!data?.modifier_group_ids?.length;

  const hasNestedModifiers = isSelected && !!data?.modifier_group_ids?.length;

  function toggleModifier(): void {
    if (isSelected && !hasNestedModifiers) {
      return;
    }

    if (!data.is_available) {
      return;
    }

    toggle(data);
  }

  function getPrice() {
    const isDiscount: boolean = data.actual_price !== data.original_price;

    return data.is_available ? (
      <div className="flex flex-row items-center my-auto">
        <p className={priceStyle(isDiscount, data.is_available)}>
          + {formatCurrency(data.original_price * (quantity || 1))}
        </p>
        {isDiscount && <p className={highlightedPriceStyle}>+ {formatCurrency(data.actual_price * (quantity || 1))}</p>}
      </div>
    ) : null;
  }

  const modifierGroups = useMemo(
    () =>
      data.modifier_groups?.filter((modifierGroup) =>
        selectedModifiers?.some((modifier) => modifier.modifier_group_id === modifierGroup.id),
      ),
    [selectedModifiers, data.modifier_groups],
  );

  return (
    <button
      className={classnames('w-full', {
        'touchable-highlight': data.is_available,
      })}
      onClick={toggleModifier}
    >
      <div className={container}>
        <div className="flex-1 my-auto">
          <p
            className={classnames('mr-xs text-left', {
              'opacity-25': !data.is_available,
            })}
          >
            {data.name}
          </p>
          {getPrice()}
        </div>
        <div className="my-auto flex items-center gap-3">
          {isSingleSelect ? (
            <Radio checked={isSelected} />
          ) : (
            <ModifierToggleButton
              isIncreasable={canBeIncreased}
              isSelected={isSelected}
              quantity={quantity}
              increase={() => {
                if (isSelected) {
                  adjustQuantity(1);
                  return;
                }

                toggleModifier();
              }}
              decrease={() => adjustQuantity(-1)}
            />
          )}
          {hasEditButton && <BackIcon className="size-3 rotate-180" color={Theme.color.gray[700]} />}
        </div>
      </div>
      {hasNestedModifiers && <NestedModifiers data={modifierGroups} selectedModifiers={selectedModifiers} />}
    </button>
  );
}

const container =
  "flex flex-row w-full mb-md before:relative after:content-[''] after:absolute after:-top-0 after:-bottom-0";

const priceStyle = (isDiscount?: boolean, isAvailable?: boolean) =>
  classnames('text-gray-700 line-clamp-1 text-right text-xs', {
    'line-through': isDiscount,
    'opacity-25': !isAvailable,
  });

const highlightedPriceStyle = 'text-black text-right text-sm font-medium line-clamp-1 ml-xs';
